import { useEffect, useRef } from "react";
import { FileDown, FileText } from "lucide-react";
import { motion } from "motion/react";
import mapImage from "../assets/map-image.webp";
import pdfImage from "../assets/pdf-export.webp";
import travelLibraryImage from "../assets/travel-library.webp";
import travelWebsitesImage from "../assets/travel-websites.webp";
import DayPlannerCard from "../imports/DayPlannerCard-3-762";
// Bundled, not a video.stippl.io link: renders there belong to a workspace's Movies page and get deleted with it.
import reelVideo from "../../../../img/video/b2b-trip.mp4";

interface FeatureCardsProps {
	currentFeature: string;
}

const featureCards: Record<
	string,
	{
		title: string;
		position: string;
		topPosition: string;
		rightOffset?: string;
		isRawComponent?: boolean;
		height?: string;
		content: React.ReactNode;
	}
> = {
	"travel websites": {
		title: "Travel Websites",
		position: "left",
		topPosition: "top-6 md:top-12",
		height: "h-[100px] md:h-[280px]",
		content: (
			<div className="flex flex-col h-full bg-white rounded-[6px] overflow-hidden">
				<div className="flex-1 relative overflow-hidden">
					<img loading="eager" decoding="async" src={travelWebsitesImage} alt="Travel Website Preview" className="w-full h-full object-cover" />
				</div>
			</div>
		),
	},
	"full itineraries": {
		title: "Day-by-Day Itinerary",
		position: "right",
		topPosition: "top-[-3px] md:top-[-20px]",
		rightOffset: "right-[-216px] md:right-[-240px]",
		isRawComponent: true,
		content: (
			<div className="scale-[0.35] w-[300px] h-[400px] 	 md:w-[464px] md:h-[510px] md:scale-[0.6] origin-top-left">
				<DayPlannerCard />
			</div>
		),
	},
	"travel reels": {
		title: "Social Media Reel",
		position: "left",
		topPosition: "top-6 md:top-24",
		height: "h-[130px] md:h-[300px]",
		rightOffset: "right-[-16px] md:right-[-240px]",
		content: null, // rendered via TravelReelVideo component
	},
	"pdf exports": {
		title: "PDF Document",
		position: "right",
		topPosition: "top-[-10px] md:top-20",
		height: "h-[160px] md:h-[280px]",
		content: (
			<div className="flex flex-col h-full bg-white rounded-[6px] overflow-hidden pl-[1px] pt-[2px] md:pt-1">
				<div className="flex-1 relative overflow-hidden">
					<img loading="eager" decoding="async" src={pdfImage} alt="PDF Export Preview" className="w-full h-full object-cover ml-[-1px] rounded-[7px]" />
				</div>
				<div className="px-0 py-[4px] md:px-3 md:py-2.5 bg-white ">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-1 md:gap-2">
							<FileText className="w-[8px] h-[8px] md:w-4 md:h-4 text-purple-600" />
							<span className="text-[8px] md:text-[11px] font-semibold text-gray-800">Sri_Lanka_Journey.pdf</span>
						</div>
						<FileDown className="w-3.5 h-3.5 text-gray-400" />
					</div>
				</div>
			</div>
		),
	},
	"embed maps": {
		title: "Embed Maps",
		position: "left",
		topPosition: "top-4 md:top-24",
		content: (
			<div className="flex flex-col h-full overflow-hidden">
				<div className="flex-1 relative overflow-hidden">
					<motion.img
						src={mapImage}
						alt="Interactive map"
						className="w-full h-full object-cover"
						animate={{
							scale: [1, 1.08, 1],
						}}
						transition={{
							duration: 6,
							repeat: Infinity,
							ease: "easeInOut",
						}}
					/>
					<div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none" />
				</div>
				<div className="p-[2px] md:px-3.5 md:py-2.5 bg-white">
					<div className="text-[8px] md:text-[13px] font-bold text-gray-900">Embed Maps</div>
					<div className="text-[6px] md:text-[9.5px] text-gray-600 font-mono leading-tight">&lt;iframe src="https://embed.stippl.io/6992..." /&gt;</div>
				</div>
			</div>
		),
	},
	"your travel library": {
		title: "Travel Library",
		position: "right",
		topPosition: "top-5 md:top-20",
		height: "h-auto",
		content: (
			<div className="bg-white rounded-[6px] overflow-hidden">
				<img loading="eager" decoding="async" src={travelLibraryImage} alt="Travel Library" className="w-full h-auto" />
			</div>
		),
	},
};

function TravelReelVideo({ isActive }: { isActive: boolean }) {
	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;
		if (isActive) {
			video.currentTime = 0;
			video.play().catch(() => {});
		} else {
			video.pause();
		}
	}, [isActive]);

	return (
		<div className="flex flex-col h-full bg-black rounded-[6px] overflow-hidden">
			<video
				ref={videoRef}
				src={reelVideo}
				muted
				playsInline
				loop
				className="w-full h-full object-cover"
			/>
		</div>
	);
}

export function FeatureCards({ currentFeature }: FeatureCardsProps) {
	return (
		<>
			{Object.entries(featureCards).map(([key, card]) => {
				const isActive = key === currentFeature;
				const isRawComponent = "isRawComponent" in card && card.isRawComponent;

				const positionClass =
					card.position === "left"
						? `left-[-20px] md:left-[-60px] ${card.topPosition}`
						: `${"rightOffset" in card ? card.rightOffset : "right-[-16px] md:right-[-60px]"} ${card.topPosition}`;

				const xOffset = card.position === "left" ? -50 : 50;
				const cardContent = key === "travel reels" ? <TravelReelVideo isActive={isActive} /> : card.content;

				return (
					<motion.div
						key={key}
						className={`absolute ${positionClass} z-20`}
						animate={{
							opacity: isActive ? 1 : 0,
							scale: isActive ? 1 : 0.9,
							y: isActive ? 0 : 20,
							x: isActive ? 0 : xOffset,
						}}
						initial={false}
						transition={{ duration: 0.4 }}
						style={{ pointerEvents: isActive ? "auto" : "none" }}
					>
						{isRawComponent ? (
							cardContent
						) : (
							<div
								className={`bg-white rounded-[8.75px] w-[120px] md:w-[220px] ${"height" in card ? card.height : "h-[200px]"} shadow-[0px_20px_50px_-10px_rgba(139,92,246,0.3),0px_10px_30px_0px_rgba(0,0,0,0.15)] border-2 border-white/80`}
							>
								<div className="h-full flex flex-col overflow-clip rounded-[8.75px]">{cardContent}</div>
							</div>
						)}
					</motion.div>
				);
			})}
		</>
	);
}
